/**
 * A merchant pattern's dialogs (TMMPDM-0112; tmm-api 0194 item 7). Each action is an audited MM-D-101
 * operation; errors stay in the dialog (TMM-B4) and, on success, the list re-reads.
 *
 * - The detail: the pattern, its evidence counts and the gate checklist, each gate's result in words
 *   as sent. It offers the tab's actions (`VIEW_ACTIONS`): Ready for review Approve… and Reject…, Still
 *   short Reject…, Approved Retire….
 * - Approve…: the name and the kind, prefilled with the proposed ones and editable; the confirm says
 *   what members will see. The body carries `name` and `kind` only when they were changed. The
 *   checklist stays in view. The console never decides from the gates whether approval is allowed:
 *   tmm-api does.
 * - Reject…: a reason of 1-500 characters once trimmed, with a counter. Retire…: a confirm.
 * - A refusal is worded inside its dialog (TMMPDM-0113): `MERCHANT_PATTERN_NOT_PROMOTABLE` names the
 *   failed gates in the checklist's words, `MERCHANT_PATTERN_STATE` the state the pattern has now. A
 *   refusal that means the row moved on re-reads the list behind the dialog (`onStale`).
 *
 * Nothing here shows a member, tenant or owner: a pattern carries counts only.
 */
import { useId, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlined';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { ConfirmDialog, SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { isValidationFailed } from '@/api/errors';
import type { MerchantPatternGate, MerchantPatternKind, MerchantPatternRow } from '@/api/tmm/types';
import { formatNumber } from '@/format';
import {
  APPROVE_KINDS,
  NAME_MAX,
  REASON_MAX,
  approveBody,
  gateLabel,
  gateResult,
  isStaleRow,
  kindLabel,
  patternActionError,
  patternName,
  type PatternAction,
} from './knowledge';

/** The gate checklist, each gate with its result in words, in the order sent. */
export function GateChecklist({ gates }: { gates: readonly MerchantPatternGate[] }) {
  const { t } = useCopy();
  const headingId = useId();
  return (
    <Box>
      <Typography id={headingId} variant="subtitle2" component="h3">
        {t('knowledge.detail.gates', 'Gates')}
      </Typography>
      {gates.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('knowledge.detail.noGates', 'No gate results were sent.')}
        </Typography>
      ) : (
        <List dense aria-labelledby={headingId} disablePadding>
          {gates.map((gate) => (
            <ListItem key={gate.gate} disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {gate.passed ? (
                  <CheckCircleOutlineIcon fontSize="small" color="success" aria-hidden />
                ) : (
                  <RemoveCircleOutlineIcon fontSize="small" color="disabled" aria-hidden />
                )}
              </ListItemIcon>
              <ListItemText
                primary={gateLabel(gate.gate, t)}
                secondary={gateResult(gate.passed, t)}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}

/** The evidence counts: counts only, never who. */
function Evidence({ row, locale }: { row: MerchantPatternRow; locale: string }) {
  const { t } = useCopy();
  const items: Array<[string, number]> = [
    [t('knowledge.col.confirmations', 'Confirmations'), row.confirmations],
    [t('knowledge.col.edits', 'Edits'), row.edits],
    [t('knowledge.col.forgets', 'Forgets'), row.forgets],
    [t('knowledge.col.members', 'Members'), row.distinctMembers],
    [t('knowledge.col.tenants', 'Tenants'), row.distinctTenants],
  ];
  return (
    <Box>
      <Typography variant="subtitle2" component="h3">
        {t('knowledge.detail.evidence', 'Evidence')}
      </Typography>
      <Box
        component="dl"
        sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 2, rowGap: 0.5, m: 0 }}
      >
        {items.map(([label, value]) => (
          <Box key={label} sx={{ display: 'contents' }}>
            <Typography component="dt" variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0 }}>
              {formatNumber(value, locale)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function PatternText({ pattern }: { pattern: string }) {
  return (
    <Typography
      component="span"
      sx={(theme) => ({ ...theme.typography.mono, overflowWrap: 'anywhere' })}
    >
      {pattern}
    </Typography>
  );
}

interface DetailProps {
  row: MerchantPatternRow | null;
  /** The tab's actions (`VIEW_ACTIONS`), from the server's filter that filled it; no row field is read. */
  actions: readonly PatternAction[];
  onClose: () => void;
  onApprove: (row: MerchantPatternRow) => void;
  onReject: (row: MerchantPatternRow) => void;
  onRetire: (row: MerchantPatternRow) => void;
}

/** A pattern's detail: what it is, its evidence and its gates, and the actions its state allows. */
export function PatternDetailDialog({
  row,
  actions,
  onClose,
  onApprove,
  onReject,
  onRetire,
}: DetailProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const titleId = useId();
  return (
    <Dialog open={row !== null} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby={titleId}>
      {row !== null ? (
        <>
          <DialogTitle id={titleId}>{patternName(row, t)}</DialogTitle>
          {/* Read-only and it may scroll: keyboard-reachable as a labelled region (axe
              scrollable-region-focusable, seen in a real browser). */}
          <DialogContent role="region" aria-labelledby={titleId} tabIndex={0}>
            <Stack spacing={2}>
              <DialogContentText component="div">
                {t('knowledge.detail.pattern', 'Pattern')}: <PatternText pattern={row.pattern} />
                <br />
                {t('knowledge.detail.kind', 'Kind')}: {kindLabel(row.kind, t)}
              </DialogContentText>
              <Evidence row={row} locale={i18n.language} />
              <GateChecklist gates={row.gates} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>{t('common.close', 'Close')}</Button>
            {actions.includes('reject') ? (
              <Button onClick={() => onReject(row)}>{t('knowledge.reject.open', 'Reject…')}</Button>
            ) : null}
            {actions.includes('approve') ? (
              <Button variant="contained" onClick={() => onApprove(row)}>
                {t('knowledge.approve.open', 'Approve…')}
              </Button>
            ) : null}
            {actions.includes('retire') ? (
              <Button variant="contained" color="error" onClick={() => onRetire(row)}>
                {t('knowledge.retire.open', 'Retire…')}
              </Button>
            ) : null}
          </DialogActions>
        </>
      ) : null}
    </Dialog>
  );
}

interface ActionProps {
  row: MerchantPatternRow | null;
  onClose: () => void;
  onDone: (row: MerchantPatternRow) => void;
  /** A refusal that means the row moved on: the list behind the dialog re-reads. */
  onStale: () => void;
}

type FormProps = Omit<ActionProps, 'row'> & { row: MerchantPatternRow; titleId: string };

/** An approvable kind, or `''` when the proposal isn't one (PERSON, none chosen) and must be chosen. */
function approvableKind(kind: string | null): MerchantPatternKind | '' {
  return APPROVE_KINDS.find((code) => code === kind) ?? '';
}

export function ApprovePatternDialog({ row, onClose, onDone, onStale }: ActionProps) {
  const titleId = useId();
  return (
    <Dialog open={row !== null} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby={titleId}>
      {row !== null ? (
        <ApproveForm
          row={row}
          titleId={titleId}
          onClose={onClose}
          onDone={onDone}
          onStale={onStale}
        />
      ) : null}
    </Dialog>
  );
}

function ApproveForm({ row, titleId, onClose, onDone, onStale }: FormProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [name, setName] = useState(row.proposedName);
  // A proposed kind an approval can't set (PERSON, or none chosen) is chosen again.
  const [kind, setKind] = useState<MerchantPatternKind | ''>(approvableKind(row.kind));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [kindError, setKindError] = useState<string | null>(null);
  const shown = name.trim();

  const submit = async () => {
    const missingName = shown === '';
    const missingKind = kind === '';
    setNameError(missingName ? t('knowledge.approve.nameRequired', 'Enter a name.') : null);
    setKindError(missingKind ? t('knowledge.approve.kindRequired', 'Choose a kind.') : null);
    if (missingName || missingKind) return;
    setBusy(true);
    setError(null);
    try {
      await adminApi.approveMerchantPattern(row.id, approveBody(row, name, kind));
      onDone(row);
    } catch (failure: unknown) {
      if (isValidationFailed(failure)) {
        // A 400 can concern the name or the kind: said once, for the dialog, not on one field.
        setError(t('errors.field.rejected', "This value isn't accepted. Check it and try again."));
      } else {
        setError(
          patternActionError(
            failure,
            t,
            i18n.language,
            t('knowledge.approve.error', "Couldn't approve the pattern."),
          ),
        );
        if (isStaleRow(failure)) onStale();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogTitle id={titleId}>
        {t('knowledge.approve.title', 'Approve a merchant pattern')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <DialogContentText component="div">
            {t('knowledge.detail.pattern', 'Pattern')}: <PatternText pattern={row.pattern} />
          </DialogContentText>
          <TextField
            label={t('knowledge.approve.name', 'Name')}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setNameError(null);
              setError(null);
            }}
            error={nameError !== null}
            helperText={
              nameError ?? t('knowledge.approve.nameHint', 'Correct the spelling if needed.')
            }
            slotProps={{ htmlInput: { maxLength: NAME_MAX } }}
          />
          <TextField
            select
            label={t('knowledge.approve.kind', 'Kind')}
            value={kind}
            onChange={(event) => {
              setKind(approvableKind(event.target.value));
              setKindError(null);
              setError(null);
            }}
            error={kindError !== null}
            helperText={kindError ?? ' '}
          >
            {APPROVE_KINDS.map((code) => (
              <MenuItem key={code} value={code}>
                {kindLabel(code, t)}
              </MenuItem>
            ))}
          </TextField>
          <GateChecklist gates={row.gates} />
          {shown !== '' && kind !== '' ? (
            <Alert severity="info">
              {t(
                'knowledge.approve.consequence',
                'Members whose bank text matches this pattern will see it named “{{name}}” ({{kind}}).',
                { name: shown, kind: kindLabel(kind, t) },
              )}
            </Alert>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <SubmitButton busy={busy} onClick={() => void submit()}>
          {t('knowledge.approve.confirm', 'Approve')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}

export function RejectPatternDialog({ row, onClose, onDone, onStale }: ActionProps) {
  const titleId = useId();
  return (
    <Dialog open={row !== null} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby={titleId}>
      {row !== null ? (
        <RejectForm
          row={row}
          titleId={titleId}
          onClose={onClose}
          onDone={onDone}
          onStale={onStale}
        />
      ) : null}
    </Dialog>
  );
}

function RejectForm({ row, titleId, onClose, onDone, onStale }: FormProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  // The bound is on the trimmed reason (1-500 characters), so the counter counts it.
  const used = reason.trim().length;
  const tooLong = used > REASON_MAX;

  const submit = async () => {
    const trimmed = reason.trim();
    if (trimmed === '') {
      setFieldError(t('knowledge.reject.required', 'Enter a reason.'));
      return;
    }
    if (tooLong) {
      setFieldError(
        t('knowledge.reject.tooLong', 'Use {{max, number}} characters or fewer.', {
          max: REASON_MAX,
        }),
      );
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await adminApi.rejectMerchantPattern(row.id, { reason: trimmed });
      onDone(row);
    } catch (failure: unknown) {
      if (isValidationFailed(failure)) {
        setFieldError(
          t('errors.field.rejected', "This value isn't accepted. Check it and try again."),
        );
      } else {
        setError(
          patternActionError(
            failure,
            t,
            i18n.language,
            t('knowledge.reject.error', "Couldn't reject the pattern."),
          ),
        );
        if (isStaleRow(failure)) onStale();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogTitle id={titleId}>
        {t('knowledge.reject.title', 'Reject “{{name}}”', { name: patternName(row, t) })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <DialogContentText component="div">
            {t('knowledge.detail.pattern', 'Pattern')}: <PatternText pattern={row.pattern} />
          </DialogContentText>
          <TextField
            required
            multiline
            minRows={2}
            label={t('knowledge.reject.reason', 'Reason')}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              setFieldError(null);
              setError(null);
            }}
            error={fieldError !== null || tooLong}
            helperText={
              fieldError ??
              t(
                'knowledge.reject.hint',
                'Kept with the audit record. Members never see it. {{used, number}} of {{max, number}} characters.',
                { used, max: REASON_MAX },
              )
            }
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <SubmitButton busy={busy} color="error" onClick={() => void submit()}>
          {t('knowledge.reject.confirm', 'Reject')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}

/** Retire…: an approved pattern leaves use. */
export function RetirePatternDialog({ row, onClose, onDone, onStale }: ActionProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = () => {
    setError(null);
    onClose();
  };
  const confirm = async (target: MerchantPatternRow) => {
    setBusy(true);
    setError(null);
    try {
      await adminApi.retireMerchantPattern(target.id);
      onDone(target);
    } catch (failure: unknown) {
      setError(
        patternActionError(
          failure,
          t,
          i18n.language,
          t('knowledge.retire.error', "Couldn't retire the pattern."),
        ),
      );
      if (isStaleRow(failure)) onStale();
    } finally {
      setBusy(false);
    }
  };
  return (
    <ConfirmDialog
      open={row !== null}
      title={t('knowledge.retire.title', 'Retire “{{name}}”?', {
        name: row === null ? '' : patternName(row, t),
      })}
      consequence={t(
        'knowledge.retire.consequence',
        "Members' naming stops using this pattern, and it moves to Retired.",
      )}
      confirmLabel={t('knowledge.retire.confirm', 'Retire')}
      destructive
      busy={busy}
      error={error}
      onConfirm={() => {
        if (row !== null) void confirm(row);
      }}
      onCancel={close}
    />
  );
}
